/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * 31 Milk St # 960789 Boston, MA 02196 USA.
 *
 * Copyright (c) 2021-2026 (original work) Open Assessment Technologies SA;
 */

import router from '@/core/router/router';
import routingTable from '@/core/routingTable/routingTable';
import { __ } from '@oat-sa-private/ui-core';
import { appLocaleFallback, locale, normalizeLocaleCode } from '@/core/utils/locale';
import { ltiClaims } from '@/core/utils/consts';
import { accessToken as accessTokenStore, decodedAccessToken as decodedAccessTokenStore } from '@/store/accessToken';
import { get } from 'svelte/store';
import { initWebSocket } from '@/core/webSocket/webSocketHandler';
import env from '@/config/env';
import { user } from '@/store/auth';
import { refreshAccessToken } from './core/accessToken/accessToken';
import { registerTenantConfigurationRefreshListeners } from '@/core/utils/tenantConfigurationRefresh.js';

//register the dictionary loaders with dictionary content for the requested locale
__.setDictionaryLoader(requestedLocale =>
    locale
        .loadDictionary(requestedLocale)
        .catch(() => locale.loadDictionary(appLocaleFallback))
        .then(dictionaryModule => dictionaryModule.default)
);
__.setP11nRulesLoader(requestedLocale =>
    locale
        .loadDictionary(requestedLocale)
        .catch(() => locale.loadDictionary(appLocaleFallback))
        .then(dictionaryModule => dictionaryModule.p11nRules)
);
Promise.resolve()
    .then(async () => {
        try {
            await refreshAccessToken();
        } catch {
            // the routes like /error are not always supposed to contain the token, as they can be formed by backend
        }
        if (get(accessTokenStore)) {
            const jwtToken = get(decodedAccessTokenStore);
            const rawCode =
                (jwtToken[ltiClaims.launch_presentation] && jwtToken[ltiClaims.launch_presentation]['locale']) ||
                appLocaleFallback;
            const code = normalizeLocaleCode(rawCode);
            await locale.setApplicationLocale(code);
            locale.setLangAttribute(code);

            registerTenantConfigurationRefreshListeners();
        }
    })
    .then(() => {
        user.updateInfo();
        router.start(routingTable);

        initWebSocket(env('WS_URL'));
    })
    // eslint-disable-next-line no-console
    .catch(console.error);
